/**
 * ChurchCareLaunchChecklist — Iter-102 · Item 7.
 *
 * Marketing Department manual pre-campaign verification checklist for
 * the NECS ChurchCare service line. Every item is manually checkable
 * by the Marketing Director. No automated triggers. Checked state
 * persists per-user in localStorage so the Director can walk away and
 * come back to their in-progress checklist.
 */
import { useEffect, useState } from 'react';
import { apiSafeFetch, apiFetch } from '../../lib/apiFetch';
import { Card } from '../ui/card';
import { Church, CheckSquare, Square } from 'lucide-react';

const ITEMS = [
  { key: 'service_line_live',   label: 'Service line tile confirmed live on the General Commercial packages page' },
  { key: 'registration_type',   label: 'Church / House of Worship confirmed on the property registration page' },
  { key: 'quote_flow',          label: 'Quote request flow confirmed working for ChurchCare' },
  { key: 'resend_template',     label: 'Resend email template for ChurchCare inquiries confirmed active' },
  { key: 'regional_available',  label: 'Regional service availability confirmed with Ops for launch markets' },
];

const STORAGE_KEY = 'necs.marketing.churchcare_checklist.v1';

export default function ChurchCareLaunchChecklist() {
  const [checked, setChecked] = useState({});
  const [launchState, setLaunchState] = useState({ launched: false, launching: false, launch: null });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setChecked(JSON.parse(raw));
    } catch { /* ignore */ }
    // Hydrate existing launch state from backend so the UI never shows
    // "ready to launch" for a service line that's already live.
    apiSafeFetch('/api/marketing/church-care/status').then(({ data }) => {
      if (data) setLaunchState((s) => ({ ...s, launched: !!data.launched, launch: data.launch || null }));
    });
  }, []);

  const toggle = (key) => {
    setChecked((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const completed = ITEMS.filter((i) => checked[i.key]).length;
  const total = ITEMS.length;
  const pct = Math.round((completed / total) * 100);

  // Iter-103 · Item 10 — Auto-fire the campaign launch the moment the
  // Director completes the 5th item. Backend is idempotent so re-runs
  // are safe.
  useEffect(() => {
    if (pct !== 100 || launchState.launched || launchState.launching) return;
    setLaunchState((s) => ({ ...s, launching: true }));
    (async () => {
      try {
        const res = await apiFetch('/api/marketing/church-care/launch', { method: 'POST' });
        setLaunchState({
          launched: true,
          launching: false,
          launch: res?.launch || null,
        });
      } catch {
        setLaunchState((s) => ({ ...s, launching: false }));
      }
    })();
  }, [pct, launchState.launched, launchState.launching]);

  return (
    <Card className="p-5 bg-zinc-900 border-zinc-800 text-white" data-testid="marketing-church-launch-checklist">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Church className="h-5 w-5" style={{ color: '#ec4899' }} />
          <div>
            <h3 className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              NECS CHURCH CARE — LAUNCH CHECKLIST
            </h3>
            <p className="text-[11px] text-zinc-400">
              Manual pre-campaign verification. Marketing Director confirms each step before campaigns go live.
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-black leading-tight" style={{ color: pct === 100 ? '#22c55e' : '#ec4899' }}>
            {pct}%
          </p>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">
            {completed} / {total}
          </p>
        </div>
      </div>

      <ul className="space-y-2" data-testid="marketing-church-launch-list">
        {ITEMS.map((item) => {
          const done = !!checked[item.key];
          return (
            <li key={item.key}>
              <button
                type="button"
                onClick={() => toggle(item.key)}
                className="w-full flex items-start gap-3 rounded-md px-3 py-2 text-left transition"
                style={{
                  backgroundColor: done ? 'rgba(34, 197, 94, 0.08)' : 'rgba(63, 63, 70, 0.35)',
                  border: `1px solid ${done ? 'rgba(34, 197, 94, 0.5)' : '#3f3f46'}`,
                }}
                data-testid={`marketing-church-launch-item-${item.key}`}
                data-checked={done}
              >
                {done ? (
                  <CheckSquare className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#22c55e' }} />
                ) : (
                  <Square className="h-4 w-4 mt-0.5 flex-shrink-0 text-zinc-500" />
                )}
                <span
                  className={`text-sm ${done ? 'text-zinc-200 line-through decoration-zinc-500' : 'text-zinc-100'}`}
                >
                  {item.label}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {pct === 100 && (
        <div
          className="mt-4 p-3 rounded-md"
          style={{
            backgroundColor: launchState.launched ? 'rgba(34, 197, 94, 0.12)' : 'rgba(59, 130, 246, 0.12)',
            border: `1px solid ${launchState.launched ? '#22c55e' : '#3b82f6'}`,
          }}
          data-testid="marketing-church-launch-ready"
          data-launched={launchState.launched}
        >
          {launchState.launched ? (
            <p className="text-sm font-bold text-emerald-300">
              ✓ Campaign live — launched at {launchState.launch?.launched_at?.slice(0, 16) || 'now'}.
            </p>
          ) : launchState.launching ? (
            <p className="text-sm font-bold text-blue-300">Activating campaign…</p>
          ) : (
            <p className="text-sm font-bold text-emerald-300">
              All checks complete — campaigns cleared for launch.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
